/**
 * DualAuthContext — Supports both Okta and Cognito authentication.
 *
 * - Configures Okta Security provider (SSO for @nepgroup.com users)
 * - Configures Amplify for Cognito (email/password for external users)
 * - Enforces @nepgroup.com → Okta rule
 * - Fetches user profile from AnyPlay's /api/auth/me endpoint
 * - Auto-creates user record on first login
 */

import { useState, useEffect, useRef, type ReactNode } from "react";
import { useOktaAuth } from "@okta/okta-react";
import { signIn, signOut, getCurrentUser, fetchAuthSession } from "aws-amplify/auth";
import { AuthContext, type AuthContextType } from "./AuthContext";
import { api, registerTokenGetter } from "../utils/api";
import { UserRole, type UserData } from "../models/types";
import { isNepEmail } from "../authMode";

type ActiveAuth = "okta" | "cognito" | null;

interface AuthMeResponse {
  user: UserData;
  authIssuer: string;
}

export function DualAuthProvider({ children }: { children: ReactNode }) {
  const { oktaAuth, authState: oktaAuthState } = useOktaAuth();

  const [activeAuth, setActiveAuth] = useState<ActiveAuth>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [user, setUser] = useState<UserData | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [businessUnitId, setBusinessUnitId] = useState<string | null>(null);

  const bootstrapAttempted = useRef(false);

  const isAdmin = userRole === UserRole.ADMIN || userRole === UserRole.SUPERADMIN;
  const isSuperAdmin = userRole === UserRole.SUPERADMIN;

  // Register token getter for API calls
  useEffect(() => {
    registerTokenGetter(async () => {
      if (activeAuth === "okta") {
        const tokenManager = oktaAuth.tokenManager;
        const tokens = await tokenManager.getTokens();
        return tokens.accessToken?.accessToken || null;
      }
      if (activeAuth === "cognito") {
        try {
          const session = await fetchAuthSession();
          return session.tokens?.idToken?.toString() || null;
        } catch {
          return null;
        }
      }
      return null;
    });
  }, [activeAuth, oktaAuth]);

  /** Fetch user profile from AnyPlay backend */
  const fetchUserProfile = async (): Promise<UserData | null> => {
    try {
      const response = await api.get<AuthMeResponse>("/api/auth/me");
      return response.user;
    } catch (err) {
      console.error("Failed to fetch user profile:", err);
      return null;
    }
  };

  /** Apply user data to state */
  const applyUserData = (userData: UserData) => {
    setUser(userData);
    setUserRole(userData.role as UserRole);
    setBusinessUnitId(userData.businessUnitId || null);
  };

  // Bootstrap: check Okta first, then Cognito
  useEffect(() => {
    if (oktaAuthState === null) return; // Wait for Okta to resolve
    if (oktaAuthState?.isAuthenticated && isAuthenticated && activeAuth === "okta") return;
    if (bootstrapAttempted.current && !oktaAuthState?.isAuthenticated) return;

    bootstrapAttempted.current = true;

    const bootstrap = async () => {
      setIsAuthenticating(true);

      // Check Okta first
      if (oktaAuthState?.isAuthenticated) {
        setActiveAuth("okta");
        setIsAuthenticated(true);

        // Register token getter immediately so fetchUserProfile can authenticate
        registerTokenGetter(async () => {
          const tokenManager = oktaAuth.tokenManager;
          const tokens = await tokenManager.getTokens();
          return tokens.accessToken?.accessToken || null;
        });

        // Fetch user profile from backend
        const userData = await fetchUserProfile();
        if (userData) {
          applyUserData(userData);
        }
        setIsAuthenticating(false);
        return;
      }

      // Check Cognito
      try {
        await getCurrentUser();
        setActiveAuth("cognito");
        setIsAuthenticated(true);

        // Register token getter immediately so fetchUserProfile can authenticate
        registerTokenGetter(async () => {
          try {
            const session = await fetchAuthSession();
            return session.tokens?.idToken?.toString() || null;
          } catch {
            return null;
          }
        });

        // Fetch user profile from backend
        const userData = await fetchUserProfile();
        if (userData) {
          // Enforce: @nepgroup.com users must use Okta
          if (isNepEmail(userData.email)) {
            await signOut();
            setIsAuthenticated(false);
            setActiveAuth(null);
            sessionStorage.setItem("okta-required", "true");
            setIsAuthenticating(false);
            return;
          }
          applyUserData(userData);
        }
      } catch {
        // Not authenticated via either method
        setIsAuthenticated(false);
        setActiveAuth(null);
      }

      setIsAuthenticating(false);
    };

    bootstrap();
  }, [oktaAuthState]);

  /** Login via Cognito email/password */
  const login = async (email: string, password: string) => {
    // Enforce: @nepgroup.com must use Okta
    if (isNepEmail(email)) {
      throw new Error("NEP Group users must sign in with Okta SSO.");
    }

    try {
      await signIn({ username: email, password });
      setActiveAuth("cognito");
      setIsAuthenticated(true);

      // Fetch user profile from backend
      const userData = await fetchUserProfile();
      if (userData) {
        applyUserData(userData);
      }
    } catch (err) {
      throw err;
    }
  };

  /** Trigger Okta login redirect */
  const loginWithOkta = () => {
    oktaAuth.signInWithRedirect({ originalUri: "/" });
  };

  /** Logout from whichever auth method is active */
  const logout = async () => {
    try {
      if (activeAuth === "okta") {
        await oktaAuth.signOut();
      } else if (activeAuth === "cognito") {
        await signOut();
      }
    } catch (err) {
      console.error("Logout error:", err);
    }

    setUser(null);
    setIsAuthenticated(false);
    setUserRole(null);
    setBusinessUnitId(null);
    setActiveAuth(null);
    bootstrapAttempted.current = false;
  };

  // ── Idle logout timer (2 hours of inactivity) ──────────────────────
  useEffect(() => {
    if (!isAuthenticated) return;

    const IDLE_TIMEOUT_MS = 2 * 60 * 60 * 1000; // 2 hours
    let timer: ReturnType<typeof setTimeout>;

    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        console.log("[auth] idle timeout reached (2h), logging out");
        logout();
      }, IDLE_TIMEOUT_MS);
    };

    const events = ["mousedown", "keydown", "scroll", "touchstart", "pointermove"];
    events.forEach((event) => window.addEventListener(event, resetTimer, { passive: true }));
    resetTimer();

    return () => {
      clearTimeout(timer);
      events.forEach((event) => window.removeEventListener(event, resetTimer));
    };
  }, [isAuthenticated]);

  /** Refresh user data from backend */
  const refreshUserData = async () => {
    const userData = await fetchUserProfile();
    if (userData) {
      applyUserData(userData);
    }
  };

  const value: AuthContextType = {
    isAuthenticated,
    isAuthenticating,
    user,
    userRole,
    businessUnitId,
    isAdmin,
    isSuperAdmin,
    activeAuth,
    login,
    loginWithOkta,
    logout,
    refreshUserData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
